import { useMemo } from "react";

import {
  ColumnDef,
  getCoreRowModel,
  useReactTable
} from "@tanstack/react-table";

import TransposedTable from "@/components/analysisSummary/filterByGrid/transposedTable";
import { ChartSeries } from "@/components/ui/chart/utils";

export type WidgetTableRow = Record<string, string | number>;

interface DatasetWidgetTableProps {
  /** Rows shaped exactly like the ones the chart renders, already limited. */
  readonly rows: WidgetTableRow[];
  /** Row key holding the dimension value — rendered as the first column. */
  readonly categoryKey: string;
  /** Display name of the dimension column. */
  readonly categoryHeader: string;
  /** The same series the chart renders — one value column each. */
  readonly series: ChartSeries[];
  /**
   * Aggregation applied to the measure (e.g. "SUM"). Appended to the header as
   * `Measure (SUM)` on single-series tables, mirroring ChartLegend.
   */
  readonly aggregation?: string;
}

/** Measures are always right-aligned numbers — thousand separated, `-` when empty. */
const formatMeasureValue = (value: string | number | null): string => {
  if (value === null || value === undefined || value === "") return "-";
  const numericValue = Number(value);
  return Number.isNaN(numericValue)
    ? String(value)
    : numericValue.toLocaleString();
};

/**
 * Tabular view of a widget's chart data. Columns follow the chart: the dimension
 * on the left, then one column per chart series, so both views always agree.
 */
export default function DatasetWidgetTable({
  rows,
  categoryKey,
  categoryHeader,
  series,
  aggregation
}: DatasetWidgetTableProps) {
  const columns = useMemo<ColumnDef<WidgetTableRow>[]>(() => {
    // Only a single-series (ungrouped) chart appends the aggregation to its label.
    const isSingleSeries = series.length === 1;

    return [
      {
        id: categoryKey,
        accessorKey: categoryKey,
        header: categoryHeader
      },
      ...series.map((serie): ColumnDef<WidgetTableRow> => {
        const baseLabel = serie.label ?? serie.dataKey;
        return {
          id: serie.dataKey,
          accessorKey: serie.dataKey,
          header:
            isSingleSeries && aggregation
              ? `${baseLabel} (${aggregation})`
              : baseLabel,
          cell: ({ getValue }) =>
            formatMeasureValue(getValue<string | number>())
        };
      })
    ];
  }, [categoryKey, categoryHeader, series, aggregation]);

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  // The chart already caps how many rows it renders, so the table shows them all.
  return <TransposedTable table={table} limit={rows.length} />;
}
